import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';

import { getDocumentName, getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType, GenericStatusId } from '@e-socna/data-layer';

import { useGraphQLMutation } from '../../hooks/useGraphQLMutation.hook';
import { SpeedDial } from '../../components/SpeedDial';
import { RightDrawer } from '../../components/RightDrawer';
import { Admin as AdminCmp } from '../../components/Admin';
import {
  PATH_MATERIAL_ADMIN,
  PATH_MATERIAL_ADMIN_EDIT,
} from '../../app/constants/paths.constant';
import { GenericAdminForm } from '../../forms/GenericAdmin';
import { memoForm2ApiMapper } from '../../forms/MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { useMemos } from '../../forms/MemoBeforeSubmit/hooks/useMemos.hook';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import { MaterialListCmp_AllMaterials_QueryDocument } from '../../components/MaterialList/hooks/useFindAllMaterialsQuery.hook';
import type { GenericAdminFormValues } from '../../forms/GenericAdmin';

import { MaterialPage_OneMaterial_QueryDocument } from './hooks/useFindOneMaterialQuery.hook';

export const MaterialPage_Admin_Material_Fragment = graphql(`
  fragment MaterialPage_Admin_Material_Fragment on Material {
    _id
    statusId
    createdAt
    creatorId
    updatedAt
    updaterId
  }
`);

export const MaterialPage_Admin_Material_Foapu_Mutation = graphql(`
  mutation MaterialPage_Admin_Material_Foapu(
    $filter: IdFilterInput!
    $update: MaterialPartialInput!
    $versionData: VersionDataInput
  ) {
    findOneAndPartialUpdateMaterial(
      filter: $filter
      update: $update
      versionData: $versionData
    ) {
      _id
    }
  }
`);

interface Props {
  readonly material: FragmentType<typeof MaterialPage_Admin_Material_Fragment>;
}

export const Admin = ({ material: pMaterial }: Readonly<Props>) => {
  const material = getFragmentData(
    MaterialPage_Admin_Material_Fragment,
    pMaterial,
  );

  const navigate = useNavigate();

  const { paths, currentPathKey } = useBuildPaths(
    {
      consult: PATH_MATERIAL_ADMIN,
      edit: PATH_MATERIAL_ADMIN_EDIT,
    },
    { id: material._id },
  );

  const isInEditionMode = currentPathKey === 'edit';
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  const { enqueueSnackbar } = useSnackbar();

  const { clearMemos, memos, setMemos } = useMemos();

  // mutations
  const { mutate: foapuMaterialMutation } = useGraphQLMutation(
    MaterialPage_Admin_Material_Foapu_Mutation,
  );

  // on success
  const queryClient = useQueryClient();

  const mutate = useCallback(
    (
      values: GenericAdminFormValues,
      handleError: (error: unknown) => unknown,
      handleSuccess: () => unknown,
    ) => {
      const onSuccess = () => {
        leaveEditionMode();
        clearMemos();
        enqueueSnackbar('Modification enregistrée.');
        handleSuccess();
        void queryClient.invalidateQueries({
          queryKey: [
            getDocumentName(MaterialPage_OneMaterial_QueryDocument),
            { id: material._id },
          ],
        });
        void queryClient.invalidateQueries({
          queryKey: [
            getDocumentName(MaterialListCmp_AllMaterials_QueryDocument),
          ],
        });
      };

      foapuMaterialMutation(
        {
          filter: {
            _id: material._id,
          },
          update: values,
          versionData: memoForm2ApiMapper(memos),
        },
        {
          onError: handleError,
          onSuccess,
        },
      );
    },
    [
      clearMemos,
      enqueueSnackbar,
      foapuMaterialMutation,
      leaveEditionMode,
      material._id,
      memos,
      queryClient,
    ],
  );

  return (
    <>
      <AdminCmp
        createdAt={material.createdAt}
        creatorId={material.creatorId}
        id={material._id}
        statusId={material.statusId}
        updatedAt={material.updatedAt}
        updaterId={material.updaterId}
      />
      <SpeedDial
        actions={[
          {
            id: 'materialAdmin',
            icon: <DisplaySettingsIcon />,
            name: 'Administrer',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur l'Administration du Matériel"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <GenericAdminForm
          clearMemos={clearMemos}
          memos={memos}
          onCancel={leaveEditionMode}
          onSubmit={mutate}
          setMemos={setMemos}
          statusId={material.statusId as GenericStatusId}
        />
      </RightDrawer>
    </>
  );
};
